import React, {useEffect, useMemo, useState} from 'react';
import {Modal} from "@mui/material";
import {getExtendedEstimates} from "../estimate/estimate.service";
import {useNavigate, useSearchParams} from "react-router-dom";
import {EstimateStatus, EstimateStatusType, EstimateWithClientAndCostDto} from "../../shared/dtos/estimate.dto";
import {logError} from "../../shared/services/logger.service";
import {isAuthRedirectError} from "../../shared/services/auth.service";
import {useToolbar} from "../../components/ToolbarContext";
import EstimateInfoComponent from "./EstimateInfoComponent";
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {
  CardSpec,
  ChipDef,
  ListRow,
  SecondaryActionButton,
  StatusListPage,
  useStatusList,
} from "../../shared/components/list-page/ListPageKit";
import {addressOneLine, displayPhone, formatMoney, prettyStatusName} from "../../shared/components/list-page/list-format";
import {ListWithRecord, useRecordRoute} from "../../shared/components/record/RecordKit";
import EstimateRecordPage from "../estimate/record/EstimateRecordPage";

// One Paper Model: an estimate is the "current face" of a record while it is
// pre-approval work, Pending Approval, or Cancelled. Once approved it advances
// to a job and surfaces on the Jobs page. The chips are Android's.
const ESTIMATE_CHIPS: ChipDef[] = [
  {label: 'In Progress', urlParam: 'inProgress'},
  // SQ-745: finished but not yet sent or confirmed, apart from the ones still being worked on.
  {label: 'Completed', urlParam: 'completed'},
  {label: 'Pending Approval', urlParam: 'pendingApproval'},
  {label: 'Canceled', urlParam: 'cancelled'},
];

/**
 * Android's grouping, plus one web fold so no live record vanishes from every
 * chip: Approved files under In Progress (an approved estimate whose job was
 * later deleted is returned with that status, orphaned). Deleted estimates
 * are left out of the page altogether, as on Android.
 */
const chipFor = (status: EstimateStatusType): string | null => {
  switch (status) {
    case EstimateStatus.EstimateNotStarted:
    case EstimateStatus.EstimateInProgress:
    case EstimateStatus.EstimateApproved:
      return 'In Progress';
    case EstimateStatus.EstimateCompleted:
      return 'Completed';
    case EstimateStatus.EstimatePendingApproval:
    case EstimateStatus.EstimatePendingReSend:
      return 'Pending Approval';
    case EstimateStatus.EstimateCancelled:
      return 'Canceled';
    default:
      return null;
  }
};

/*
 * The card's realistic widths, from the Shippy's Tree Service snapshot (1,096
 * estimates, measured in Chrome with the card's own CSS; 98th percentile, so the
 * longest 2% wrap inside their slot):
 *   name     224px at 18px  "Not Sure What Her Name Is"
 *   phone     99px at 14px  "(208) 631-0182"
 *   address  294px at 14px  "4777 Southeast 1st Avenue, New Plymouth, ID"
 *   total    156px at 14px  "Estimate total $8,888.88" (the largest real
 *                            total is $8,455; budgeted with cents)
 *   status   117px          the "Pending Approval" pill, the longest status
 * Switch points (cardSwitchPoints): one row from 1,044px, grouped from 780px.
 */
const CARD_SPEC: CardSpec = {
  title: 224,
  status: 117,
  slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 294},
    {key: 'total', group: 'money', width: 156},
  ],
};

const parseParamsEstimateId = (paramsClientId: string | null): number | 'NEW' | undefined => {
  if(!paramsClientId) return undefined;
  if(paramsClientId.toLowerCase() === "new") return 'NEW';
  const numericValue = parseInt(paramsClientId)
  return isNaN(numericValue) ? undefined : numericValue;
}

/**
 * The estimates board, as on Android: status chips + search, one card per
 * estimate titled by its client. The title button is the automatic follow-up
 * settings.
 */
function EstimateListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams()
  const toolbarContext = useToolbar();
  const {hasPermission} = usePermissions();
  const record = useRecordRoute('/estimates');
  const openRecord = record.open;
  const initialEstimateId = parseParamsEstimateId(searchParams.get("estimateId"))

  const [estimates, setEstimates] = useState<EstimateWithClientAndCostDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEstimateId, setSelectedEstimateId] = useState<number | 'NEW' | undefined>(initialEstimateId);

  async function fetchEstimates() {
    setLoading(true);
    setError(null);
    try {
      const all = await getExtendedEstimates(navigate);
      setEstimates(all.filter(estimate => estimate.status !== EstimateStatus.EstimateDeleted));
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error fetching estimates', {}, e as Error);
      setError(`Couldn't load estimates: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    toolbarContext.initToolbar('EstimateListPage', '');
    void fetchEstimates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: ListRow[] = useMemo(() => estimates.map(estimate => ({
    key: estimate.id,
    title: estimate.clientName?.trim() || estimate.estimateName?.trim() || `Estimate #${estimate.id}`,
    values: {
      phone: displayPhone(estimate.phoneNumber),
      address: addressOneLine(
        estimate.estimateAddress || estimate.clientAddress,
        estimate.estimateCity || estimate.clientCity,
        estimate.estimateState || estimate.clientState,
      ),
      total: {label: 'Estimate total', value: formatMoney(estimate.estimatedCost ?? 0)},
    },
    status: prettyStatusName(estimate.status),
    chip: chipFor(estimate.status),
    // SQ-596: every estimate opens as its own page (/estimates/12) over the list.
    onClick: () => openRecord(estimate.id),
  })), [estimates, openRecord]);

  const list = useStatusList(rows, ESTIMATE_CHIPS);

  const handleCloseEstimateInfo = () => {
    setSelectedEstimateId(undefined);
    searchParams.delete("estimateId")
    setSearchParams(searchParams);
  }

  const handleEstimateDetailSaved = async () => {
    setSelectedEstimateId(undefined);
    await fetchEstimates();
  }

  return (
    <ListWithRecord
      list={<>
      <StatusListPage
        title="Estimates"
        titleButton={hasPermission(PermissionCode.EstimatesManageFollowUps)
          ? <SecondaryActionButton label="Set automatic follow-ups" onClick={() => navigate('/estimates/automatic-follow-ups')}/>
          : undefined}
        searchLabel="Estimate Filters and Search"
        list={list}
        spec={CARD_SPEC}
        loading={loading}
        error={error}
        onRetry={() => void fetchEstimates()}
      />
      <Modal
        open={!!selectedEstimateId}
        onClose={() => handleCloseEstimateInfo()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <EstimateInfoComponent estimateId={selectedEstimateId === 'NEW' ? undefined : selectedEstimateId} onClose={() => handleCloseEstimateInfo()} onSave={() => handleEstimateDetailSaved()}/>
      </Modal>
      </>}
      record={typeof record.id === 'number' && (
        <EstimateRecordPage key={record.id} estimateId={record.id} onChanged={() => void fetchEstimates()}/>
      )}
    />
  );
}

export default EstimateListPage;
